import { useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { IAhuBoardSection, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { useDebouncedCallback } from '../../forms/formUtils';
import { cmsLengthValues, cmsSplitSections } from '../../forms/cmsDoc';
import { queueSectionPatch } from '../../forms/sectionPatchQueue';
import { checkSectionChangesAndValidate } from '../../validation/validationController';

/**
 * Phase 6e1 — the tag strip (§7.1 `.tag_wrap`, tag.tmpl markup faithful):
 * rendered under the navy panel on every non-AHU tab. "<Name> section"
 * heading (UDS→"User Defined"), 36-max tag input (800 ms debounce →
 * `change:section:tag:<id>` + `change:section:tag:canvas`), Section Length
 * select + auto-adjust checkbox with "min required" annotation; Plenum/UDS
 * hide auto-adjust; split-plenum uses split length lists + aw_length
 * fallback; auto_adjust forces length to required_length.
 */

interface TagStripProps {
  section: IAhuBoardSection;
  cms: ICmsAhuDoc | undefined;
}

const NO_AUTO_ADJUST_TYPES = ['plenum', 'split_plenum', 'uds', 'user_defined_component'];

const TagStrip = ({ section, cms }: TagStripProps) => {
  const dispatch = useAppDispatch();
  const currentTab = useAppSelector((s) => s.ahuBoard.selection.currentTab);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // tabs.container.js:329 — the heading is NOT simply the section's server
  // name. When viewdata's component_id (or component_sitting_id) points at the
  // section being rendered, legacy shows viewdata.component_name, the name the
  // toolbar sent with `create:component:any`; otherwise it falls back to
  // `current_section.get('name')`. The two only diverge for Split-Plenum, whose
  // toolbar name is 'Plenum' (toolbar.components.js:140-142) while
  // AHUSection.name is 'Split-Plenum' (models_section.py:147-150) — so legacy
  // labels a freshly inserted Split-Plenum "Plenum section". viewdata is
  // in-memory only (mvc/state/storage.js), so a reload drops the stamp and the
  // heading reverts to "Split-Plenum section"; that is legacy behaviour, not an
  // oversight of this port.
  const componentName = useAppSelector((s) => s.ahuBoard.selection.componentName);
  const componentId = useAppSelector((s) => s.ahuBoard.selection.componentId);
  const componentSittingId = useAppSelector((s) => s.ahuBoard.selection.componentSittingId);
  const stamped = componentId === section.id || componentSittingId === section.id;
  const name = String((stamped ? componentName : section.name) ?? '');
  const displayName = name === 'UDS' ? 'User Defined' : name;
  const sectionType = String(section.type ?? '').toLowerCase();
  const autoAdjustment = !NO_AUTO_ADJUST_TYPES.includes(sectionType);
  const lengthValues = cmsLengthValues(cms);
  const split = !!section.split;
  const splitLists = cmsSplitSections(cms);
  const splitLengthValues = Array.isArray(splitLists.length_values)
    ? (splitLists.length_values as Array<string | number>)
    : lengthValues;

  // auto_adjust && required_length≠length → DISPLAY-ONLY snap. Legacy does
  // `current_section.set({length: required_length})` (tabs.container.js:346)
  // — a local Backbone set, NEVER a save. The verify-flagged one-shot
  // `PATCH ahu/sections/<id>` on first load came from patching here.

  const changeTag = useDebouncedCallback((value: string) => {
    ahuBoardBus.emit(`change:section:tag:${section.id}`, value);
    ahuBoardBus.emit('change:section:tag:canvas', section.id, value);
    // Restore focus + caret (legacy changeTag) — React keeps the input
    // mounted, so focus survives; nothing to do.
  }, 800);

  const setSectionField = (changes: Record<string, unknown>) => {
    queueSectionPatch({
      dispatch,
      sectionId: section.id,
      tunnelId: section.tunnel_id,
      changes,
      onSuccess: (saved, applied) => {
        void dispatch(
          checkSectionChangesAndValidate({
            sectionId: section.id,
            tunnelId: section.tunnel_id,
            sectionLabel: String(saved.label ?? saved.name ?? ''),
            changes: applied,
            isFirstOrLast: false,
            firstComponentId: null,
          })
        );
        if ('length' in applied) {
          ahuBoardBus.emit('change:tunnel:dimensions', {
            tunnel_id: section.tunnel_id,
            render: true,
            only_for: section.id,
          });
          ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
            tunnel_id: section.tunnel_id,
            hide_from_canvas: true,
          });
          ahuBoardBus.emit('section:length:changed');
        }
      },
    });
  };

  if (currentTab === 'ahu') return null;

  const requiredLength = section.required_length;
  // Display-only auto-adjust snap (legacy local set — see note above).
  const effectiveLength =
    section.auto_adjust && requiredLength != null
      ? Number(requiredLength)
      : Number(section.length);
  const lengthSelectValues = split ? splitLengthValues : lengthValues;
  const awLengthFallback =
    split && !lengthSelectValues.some((v) => Number(v) === effectiveLength);

  // Legacy section_length = Number(length).toFixed(2) — the selection span
  // ALWAYS displays "4.00" (verify 6e2a-10); the <select> value matches the
  // length_value_names option verbatim (loose == in legacy ifCompare).
  const displayLength = Number.isFinite(effectiveLength)
    ? effectiveLength.toFixed(2)
    : '';
  const matchedLength = (() => {
    const hit = lengthSelectValues.find((v) => Number(v) === effectiveLength);
    if (hit != null) return String(hit);
    return displayLength;
  })();

  return (
    <>
      <div className="section-tag-line">
        <span>
          <span className="label">
            {displayName === 'User Defined' ? (
              <span className={`first-letter ${name}`}> User Defined </span>
            ) : (
              <>
                <span className="first-letter"> {displayName}</span> section
              </>
            )}{' '}
          </span>
          <span className="hint-for-rate hide">
            To rate the <span className="first-letter">{displayName}</span>, click on RATING
          </span>
        </span>
        <input
          ref={inputRef}
          type="text"
          name="tag"
          maxLength={36}
          className="section_field tag_name_change"
          defaultValue={String(section.tag ?? '')}
          tabIndex={-1}
          onKeyUp={(e) => changeTag(e.currentTarget.value)}
        />
      </div>
      <div className="inputs-group length-line">
        <ul className="sublist">
          <li>
            <div>
              <span className="unit">in</span>
              {/* §JSX-WS — tag.tmpl:13-59 puts a newline between every control
                  in this row, and they are inline-level, so each one is a real
                  collapsed space. `.selection` is `position:absolute`
                  (styles.css:3083) and contributes nothing to the inline flow,
                  but the spaces on either side of it collapse into one, so
                  legacy lays the row out as
                    [unit] SPACE [select] SPACE [.min-required-length] SPACE [label].
                  JSX drops newline-only text: without these the length select
                  sat 4px left of dev and the auto-adjust checkbox 8px left, on
                  EVERY section type (this strip is shared by all of them). */}
              {' '}
              {split ? (
                <>
                  <span className="selection cabinet-sizes-selection">
                    <span>{awLengthFallback ? String(section.aw_length ?? '') : displayLength}</span>
                  </span>{' '}
                  <select
                    className="section_field"
                    name="length"
                    value={awLengthFallback ? String(section.length ?? '') : matchedLength}
                    onChange={(e) => setSectionField({ length: Number(e.currentTarget.value) })}
                  >
                    {awLengthFallback && (
                      <option value={String(section.length ?? section.aw_length ?? '')}>
                        {String(section.aw_length ?? '')}
                      </option>
                    )}
                    {lengthSelectValues.map((v) => (
                      <option key={String(v)} value={String(v)}>
                        {String(v)}
                      </option>
                    ))}
                  </select>
                </>
              ) : autoAdjustment ? (
                <>
                  <span
                    className={`selection cabinet-sizes-selection${
                      section.auto_adjust ? ' non-clickable span-disabled' : ''
                    }`}
                  >
                    <span>{displayLength}</span>
                  </span>{' '}
                  <select
                    disabled={!!section.auto_adjust}
                    className={`section_field medium-input_length cabinet-sizes-selection_select-scroll cabinet-sizes-selection_select${
                      section.auto_adjust ? ' non-clickable' : ''
                    }`}
                    name="length"
                    value={matchedLength}
                    onChange={(e) => setSectionField({ length: Number(e.currentTarget.value) })}
                  >
                    {lengthValues.map((v) => (
                      <option key={String(v)} value={String(v)}>
                        {String(v)}
                      </option>
                    ))}
                  </select>{' '}
                  <div className="min-required-length">
                    <input
                      className="sublist-ctrl-checkbox section_subtab min-required-checkbox section_field-checkbox"
                      checked={!!section.auto_adjust}
                      id="auto-adjustment"
                      name="auto_adjust"
                      type="checkbox"
                      onChange={(e) => setSectionField({ auto_adjust: e.currentTarget.checked })}
                    />
                    {/* legacy truncate_decimal: trunc to 2dp then toFixed(2)
                        → "34.00 in required" (verify 6e2a-10).
                        QA6 #3: the gate is Handlebars `{{#if
                        section.required_length}}` (tab.section.tmpl:49-50),
                        whose truthiness treats **0 as FALSY** — so a zero
                        required_length must render " min required " with an
                        EMPTY span, not "0.00 in required" (verified live on
                        :8130 with a 0 injected: the legacy row reads "min
                        required"). Shushanik's report asked for the "required"
                        text to be hidden once a component is selected, but
                        legacy shows it exactly then — the text itself is parity.

                        THE SPAN IS A CHILD OF THE LABEL: tab.section.tmpl:49
                        closes the <label> with a stray `</span>`, so the parser
                        discards that end tag and nests the following span inside
                        the label. Legacy's live DOM is
                          <label class="min-required-label"> 36.00 in
                            <span> required </span></label>
                        and `.min-required-label` is `float:right`, so the value
                        and the word "required" float right TOGETHER
                        ("[x] 36.00 in required"). Rendering the span as a
                        SIBLING left it outside the float, so it was laid out
                        before the floated label ("[x] required 20.00 in" — the
                        misplacement in the QA screenshot) and it also stopped
                        being part of the label's click target. */}
                    <label className="min-required-label" htmlFor="auto-adjustment">
                      {requiredLength ? `${(Math.trunc(Number(requiredLength) * 100) / 100).toFixed(2)} in ` : ' min required '}
                      <span>{requiredLength ? ' required ' : ''}</span>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <span className="selection cabinet-sizes-selection">
                    <span>{displayLength} </span>
                  </span>{' '}
                  <select
                    id="cabinet-sizes-selection_select_id"
                    className="section_field cabinet-sizes-selection_select-scroll cabinet-sizes-selection_select"
                    name="length"
                    value={matchedLength}
                    onChange={(e) => setSectionField({ length: Number(e.currentTarget.value) })}
                  >
                    {lengthValues.map((v) => (
                      <option key={String(v)} value={String(v)}>
                        {String(v)}
                      </option>
                    ))}
                  </select>
                </>
              )}{' '}
              <span className="label">Section Length</span>
            </div>
          </li>
        </ul>
      </div>
    </>
  );
};

export default TagStrip;
